<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BookedSeats, ScheduledChange, SeatDecreaseAdjustment, SeatUsage } from '@/utils/billing';

// The booked-seats line (issue 299 D21). The Verwaltung band's compact read of
// the SAME figures SeatPanel draws on the billing page — this component owns
// only its own small pip/meter rendering (workspace-start M3); SeatPanel.vue
// stays exactly as it is, unread by anything here beyond its shape of props.
//
// Since 664b07da7 (Stella, 2026-09-19) a full booking is just all pips filled —
// no "Alle belegt" pill, no warning colour, no advice line. This component
// never had those in the first place: it only draws the pips, the two
// figures, the still-true re-floor/pending notice and the buyer's link.
const props = withDefaults(
  defineProps<{
    booked: BookedSeats | null;
    usage: SeatUsage | null;
    scheduledChanges: ScheduledChange[];
    adjustments?: SeatDecreaseAdjustment[];
    showApprentices: boolean;
    demo?: boolean;
    // Buyers only (the caller passes null for everyone else) — the same
    // '/billing?tab=plans#plan-add' string the teaser chips link to.
    manageHref?: string | null;
  }>(),
  { adjustments: () => [], demo: false, manageHref: null },
);

const { t, locale } = useI18n();

// Above this many booked seats the pips stop being countable and become
// texture — mirrors SeatPanel's own PIP_MAX.
const PIP_MAX = 40;

type Row = {
  metric: 'users' | 'apprentices';
  booked: number;
  used: number | null;
  shape: 'pips' | 'meter' | 'none';
};

function seatShape(booked: number, used: number | null): Row['shape'] {
  if (used == null || !Number.isFinite(booked) || booked <= 0) return 'none';
  return booked <= PIP_MAX ? 'pips' : 'meter';
}

const rows = computed<Row[]>(() => {
  const b = props.booked;
  if (!b) return [];
  const usage = props.usage;
  const out: Row[] = [
    { metric: 'users', booked: b.userSeats, used: usage?.users ?? null, shape: seatShape(b.userSeats, usage?.users ?? null) },
  ];
  if (props.showApprentices) {
    out.push({
      metric: 'apprentices',
      booked: b.apprenticeSeats,
      used: usage?.apprentices ?? null,
      shape: seatShape(b.apprenticeSeats, usage?.apprentices ?? null),
    });
  }
  return out;
});

// "{used} / {booked} Nutzerplätze" — or, when usage is unknown, just the
// booked figure (the useUnknown case: numbers without pips). The "/" is a
// format, not a sentence — like the date tile's "–" span, it is never
// translated; only the unit word is, and it reuses SeatPanel's OWN already-
// translated labels (billing.seats.userSeats / apprenticeSeats) rather than
// inventing a second word for the same thing in nine more catalogues.
function rowText(row: Row): string {
  const unit = row.metric === 'users' ? t('billing.seats.userSeats') : t('billing.seats.apprenticeSeats');
  return row.used == null ? `${row.booked} ${unit}` : `${row.used} / ${row.booked} ${unit}`;
}

function pendingFor(metric: 'users' | 'apprentices'): ScheduledChange | undefined {
  return props.scheduledChanges.find((c) => c.changeType === 'seat_change' && c.seatMetric === metric && c.targetSeats != null);
}
function adjustmentFor(metric: 'users' | 'apprentices'): SeatDecreaseAdjustment | undefined {
  return (props.adjustments ?? []).find((a) => a.seatMetric === metric);
}
// UTC, like SeatPanel's own formatDate — effectiveAt is a cycle boundary.
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale.value, { dateStyle: 'medium', timeZone: 'UTC' });
}

// One small note line per row, in priority order: the applied re-floor (the
// more surprising fact) beats a still-pending change, which beats "unknown",
// which beats the demo carve-out. Never more than one line per row — this is
// the compact read, not the billing page.
function rowNote(row: Row): string | null {
  const adjustment = adjustmentFor(row.metric);
  if (adjustment) {
    const vars = { requested: adjustment.requestedSeats, booked: adjustment.bookedSeats, used: adjustment.usedSeats };
    return row.metric === 'users' ? t('billing.seatPanel.reflooredUsers', vars) : t('billing.seatPanel.reflooredApprentices', vars);
  }
  const pending = pendingFor(row.metric);
  if (pending) {
    const vars = { target: pending.targetSeats ?? 0, date: formatDate(pending.effectiveAt), current: row.booked };
    return row.metric === 'users' ? t('billing.seatPanel.pendingUsers', vars) : t('billing.seatPanel.pendingApprentices', vars);
  }
  if (row.used == null) return t('billing.seatPanel.useUnknown');
  if (props.demo && row.metric === 'apprentices') return t('billing.seatPanel.demoNotCounted');
  return null;
}
const notes = computed(() => rows.value.map(rowNote).filter((n): n is string => !!n));
</script>

<template>
  <div v-if="booked" class="seats-line" data-testid="seats-line">
    <div class="seats-line-groups">
      <span v-for="(row, i) in rows" :key="row.metric" class="seats-line-group">
        <span v-if="i > 0" class="seats-line-sep" aria-hidden="true">·</span>
        <span v-if="row.shape === 'pips'" class="seats-line-pips" aria-hidden="true">
          <span
            v-for="idx in row.booked"
            :key="idx"
            class="seats-line-pip"
            :class="{ 'seats-line-pip--taken': idx <= (row.used ?? 0) }"
          />
        </span>
        <span v-else-if="row.shape === 'meter'" class="seats-line-meter" aria-hidden="true">
          <span
            class="seats-line-meter-fill"
            :style="{ width: `${Math.min(100, Math.round(((row.used ?? 0) / row.booked) * 100))}%` }"
          />
        </span>
        <span class="seats-line-text tabular-nums">{{ rowText(row) }}</span>
      </span>
      <a v-if="manageHref" :href="manageHref" class="seats-line-manage">{{ t('billing.seatPanel.manage') }}</a>
    </div>
    <p v-for="(note, i) in notes" :key="i" class="seats-line-note">{{ note }}</p>
  </div>
</template>

<style scoped>
.seats-line { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; text-align: right; }
.seats-line-groups { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; }
.seats-line-group { display: inline-flex; align-items: center; gap: 6px; }
.seats-line-sep { color: var(--text-tertiary); }
.seats-line-pips { display: inline-flex; flex-wrap: wrap; gap: 2px; max-width: 160px; }
.seats-line-pip { width: 6px; height: 6px; border-radius: 2px; background: var(--surface-wash); }
/* Taken seats read on the single-series lavender scale, same as SeatPanel's own
   pips — never confused with the free wash underneath in either theme. */
.seats-line-pip--taken { background: var(--scale-4); }
.seats-line-meter { display: inline-block; width: 48px; height: 6px; border-radius: 999px; background: var(--surface-wash); overflow: hidden; }
.seats-line-meter-fill { display: block; height: 100%; background: var(--scale-4); }
.seats-line-text { font-size: var(--text-sm); font-weight: 600; color: var(--text-heading); white-space: nowrap; }
.seats-line-manage { font-size: var(--text-xs); font-weight: 600; color: var(--text-heading); text-decoration: underline; white-space: nowrap; }
.seats-line-note { font-size: var(--text-xs); color: var(--text-secondary); max-width: 320px; }
</style>
